import React, { useState } from "react";
import { Alert, Box, Button, Divider, Paper, Typography } from "@mui/material";
import { Geometry } from "geojson";
import { noop } from "lodash";

import { selectLayerByVersionAndLayerKey } from "fond/api/layersSlice";
import Upload from "fond/project/panels/upload/Upload";
import { addImport } from "fond/redux/imports";
import { isImportInProgress } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { ConfirmModal, LinearProgressWithLabel, useStackedNavigationContext } from "fond/widgets";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

export interface MultiLayerUploadSection {
  layerId: string;
  displayName: string;
  geometryType: Geometry["type"];
}

const UploadSection: React.FC<MultiLayerUploadSection> = ({ layerId, displayName, geometryType }) => {
  const dispatch = useAppDispatch();
  const versionId = useAppSelector((state) => state.project.versionId);
  const existingLayer = useAppSelector((state) => selectLayerByVersionAndLayerKey(state, { versionId, layerId }));
  const hasExistingData = !!existingLayer;
  const importState = useAppSelector((state) => state.imports[versionId]?.[layerId]);
  const uploading = isImportInProgress(importState?.status);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const proceedWithUpload = () => {
    dispatch(
      addImport({
        versionId,
        layerKey: layerId,
        displayName,
        files: selectedFiles,
      })
    );

    setSelectedFiles([]);
    setShowConfirmModal(false);
  };

  const handleOnUpload = () => {
    if (hasExistingData) {
      setShowConfirmModal(true);
    } else {
      proceedWithUpload();
    }
  };

  return (
    <Box mb={2}>
      <Typography fontWeight="500">{displayName}</Typography>
      <ConfirmModal
        open={showConfirmModal}
        header="Replace existing data?"
        confirmText="Continue"
        cancelText="Cancel"
        color="error"
        content={
          <Typography variant="body2">This will replace all existing {displayName.toLowerCase()} data. This action cannot be undone.</Typography>
        }
        onConfirm={proceedWithUpload}
        onCancel={() => setShowConfirmModal(false)}
      />
      {uploading ? (
        <>
          <Alert severity="info" sx={{ my: 1 }}>
            FOND is now processing your data, and it will be visible on the map shortly.
          </Alert>
          <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
            <LinearProgressWithLabel color="primary" variant="indeterminate" title="Importing layer" subtext="Processing..." />
          </Paper>
        </>
      ) : (
        <>
          <Upload
            layer={{
              id: layerId,
              geometryType,
              name: displayName,
            }}
            onSelectFiles={(validFiles) => setSelectedFiles(validFiles)}
            autoUpload={false}
            onBeginUpload={noop}
            onRemove={() => setSelectedFiles([])}
            includeLayerName={false}
            convertToDualSidedField={null}
          />
          <Box display="flex" alignItems="center" justifyContent="flex-end">
            <Button
              variant="contained"
              size="small"
              disabled={selectedFiles.length === 0}
              onClick={handleOnUpload}
              sx={{ px: 2 }}
              data-testid={`upload-${layerId}-button`}
            >
              Upload
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

interface MuPowerMultiLayerUploadProps {
  title: string;
  sections: MultiLayerUploadSection[];
}

const MuPowerMultiLayerUpload: React.FC<MuPowerMultiLayerUploadProps> = ({ title, sections }) => {
  const { goBack, clear } = useStackedNavigationContext();

  return (
    <Box>
      <StackedNavigationHeader title={title} onBackButtonClick={() => goBack()} />
      <Box px={1}>
        {sections.map((section, index) => (
          <React.Fragment key={section.layerId}>
            {index > 0 && <Divider sx={{ my: 1 }} />}
            <UploadSection {...section} />
          </React.Fragment>
        ))}
        <Divider sx={{ my: 2 }} />
        <Box display="flex" alignItems="center" justifyContent="flex-end">
          <Button color="primary" size="small" onClick={() => clear()} data-testid="multi-layer-upload-done">
            Done
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default MuPowerMultiLayerUpload;
